import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiX, FiUpload, FiFile, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import './BugReportForm.scss';
import { addBugReport } from '../../../store/slices/gameTestingSlice';

const SENSITIVITY_LEVELS = [
  { value: 'low', label: 'Low - Minor visual or UX issues' },
  { value: 'medium', label: 'Medium - Functional issues that don\'t break the game' },
  { value: 'high', label: 'High - Critical issues that affect core functionality' },
  { value: 'critical', label: 'Critical - Game breaking bugs that prevent progress' }
];

const BugReportForm = ({ reportId, selectedGames, onClose }) => {
  const dispatch = useDispatch();
  const { bugReportsLoading } = useSelector(state => state.gameTesting);
  const { user } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    gameName: selectedGames[0] || '',
    sensitivity: 'medium',
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for each file
    const filesWithPreviews = files.map(file => ({
      file,
      preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
      name: file.name,
      type: file.type
    }));
    
    setSelectedFiles(prev => [...prev, ...filesWithPreviews]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      // Revoke the URL to avoid memory leaks
      if (updated[index].preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  const validateForm = () => {
    if (!formData.gameName) {
      setError('Please select a game');
      return false;
    }
    if (!formData.title.trim()) {
      setError('Please provide a title for the bug report');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Please provide a description of the bug');
      return false;
    }
    if (!formData.stepsToReproduce.trim()) {
      setError('Please provide steps to reproduce the bug');
      return false;
    }
    if (!formData.expectedBehavior.trim()) {
      setError('Please describe the expected behavior');
      return false;
    }
    if (!formData.actualBehavior.trim()) {
      setError('Please describe the actual behavior');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    if (isSubmitting || bugReportsLoading) return; // Prevent multiple submissions

    setIsSubmitting(true);

    try {
      // Convert stepsToReproduce string to array as expected by database
      const stepsArray = formData.stepsToReproduce
        .split('\n')
        .map(step => step.trim())
        .filter(step => step !== '');

      await dispatch(addBugReport({
        reportId,
        bugData: {
          ...formData,
          stepsToReproduce: stepsArray,
          submittedBy: {
            name: user?.displayName || user?.email?.split('@')[0] || 'Anonymous',
            email: user?.email || ''
          },
          status: 'pending',
          type: 'game'
        },
        files: selectedFiles.map(f => f.file)
      })).unwrap();
      
      // Clean up preview URLs
      selectedFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      
      // Show success message briefly before closing
      setSubmitSuccess(true);
      
      // Close after showing success message
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (err) {
      setError('Failed to submit bug report. Please try again.');
      console.error('Error submitting bug report:', err);
      setIsSubmitting(false);
    }
  };

  const renderFilePreview = (file, index) => {
    return (
      <div className="file-preview" key={index}>
        <div className="file-info">
          {file.type && file.type.includes('image') ? (
            <div className="image-preview">
              <img src={file.preview} alt={file.name} />
            </div>
          ) : (
            <div className="document-icon">
              <FiFile />
            </div>
          )}
          <span className="file-name">{file.name}</span>
        </div>
        
        <button 
          type="button" 
          className="remove-file" 
          onClick={() => removeFile(index)}
        >
          <FiTrash2 />
        </button>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="bug-report-form">
        <div className="modal-header">
          <h3>Report a Bug</h3>
          <button className="close-button" onClick={onClose}>
            <FiX />
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="error-message">
                <FiAlertCircle />
                <span>{error}</span>
              </div>
            )}
            
            {submitSuccess && (
              <div className="success-message">
                <span>✓ Bug report submitted successfully! Returning to edit form...</span>
              </div>
            )}
            
            <div className="form-section">
              <label>Game*</label>
              <select 
                name="gameName"
                value={formData.gameName}
                onChange={handleInputChange}
                className="form-select"
                required
              >
                <option value="">Select Game</option>
                {selectedGames.map((game, index) => (
                  <option key={index} value={game}>{game}</option>
                ))}
              </select>
            </div>
            
            <div className="form-section">
              <label>Bug Title*</label>
              <input 
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief description of the bug"
                className="form-input"
                required
              />
            </div>
            
            <div className="form-section">
              <label>Sensitivity Level*</label>
              <div className="sensitivity-options">
                {SENSITIVITY_LEVELS.map((level) => (
                  <div className="sensitivity-option" key={level.value}>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="sensitivity"
                        value={level.value}
                        checked={formData.sensitivity === level.value}
                        onChange={handleInputChange}
                      />
                      <span className={`sensitivity-badge ${level.value}`}>{level.value}</span>
                      <span className="sensitivity-description">{level.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="form-section">
              <label>Bug Description*</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Detailed description of what's happening"
                className="form-textarea"
                required
              ></textarea>
            </div>
            
            <div className="form-section">
              <label>Steps to Reproduce*</label>
              <textarea
                name="stepsToReproduce"
                value={formData.stepsToReproduce}
                onChange={handleInputChange}
                rows={3}
                placeholder="1. Open the game&#10;2. Click on start button&#10;3. Navigate to settings..."
                className="form-textarea"
                required
              ></textarea>
            </div>
            
            <div className="form-section">
              <label>Expected Behavior*</label>
              <textarea
                name="expectedBehavior"
                value={formData.expectedBehavior}
                onChange={handleInputChange}
                rows={2}
                placeholder="What should have happened"
                className="form-textarea"
                required
              ></textarea>
            </div>
            
            <div className="form-section">
              <label>Actual Behavior*</label>
              <textarea
                name="actualBehavior"
                value={formData.actualBehavior}
                onChange={handleInputChange}
                rows={2}
                placeholder="What actually happened"
                className="form-textarea"
                required
              ></textarea>
            </div>
            
            <div className="form-section">
              <label>Supporting Evidence</label>
              <div className="file-upload-container">
                <label className="file-upload-label">
                  <input 
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <span className="upload-icon"><FiUpload /></span>
                  Upload Screenshots/Files
                </label>
                
                {selectedFiles.length > 0 && (
                  <div className="selected-files">
                    {selectedFiles.map((file, index) => 
                      renderFilePreview(file, index)
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-outline-secondary"
                onClick={onClose}
                disabled={isSubmitting || submitSuccess}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || submitSuccess}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-sm"></div>
                    Submitting...
                  </>
                ) : submitSuccess ? (
                  <>
                    ✓ Bug Submitted!
                  </>
                ) : (
                  'Submit Bug Report'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BugReportForm; 